import { observer } from 'mobx-react';
import { FC, useCallback, useMemo, useState } from 'react';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { ColumnSort } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import styles from '../../../styles/pages.module.scss';
import myLinksTableStyles from './my-links.page.module.scss';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IMFPPStore } from '../../../modules/stores';
import { useMyLinksList } from '../../../modules/hooks/apis/use-my-links-list';
import { useMyLinksHeaders } from '../../../modules/hooks/theaders/my-links.headers';
import { useMyLinksActions } from '../../../modules/hooks/actions/my-links.actions';
import { IPublisherLink } from '../../../modules/models/link.model';
import { StopLinkModal } from '../../../modules/components/modals/stop-link-modal';
import { RegenerateLinkModal } from '../../../modules/components/modals/regenerate-link-modal';
import { LinksApi, ILinksApiCallError } from '../../../modules/apis/links.api';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';
import { IAskError } from '@monorepo/tools/src/lib/tools/ask/ask';
import { DispatchLogsTypes } from '@monorepo/controlled/src/hooks/use-logs';
import { sendGtagEvent } from '@monorepo/tools/src/lib/tools/tracking';
import { TrackingActions } from '@monorepo/tools/src/lib/consts/tracking/actions';
import { EVENTS } from '@monorepo/tools/src/lib/consts/tracking/events';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { IAccount } from '../../../modules/models/account.model';

const defaultSortBy: ColumnSort[] = [{ id: 'createdAt', desc: true }];

function csvEscape(value: unknown): string {
	if (value === null || value === undefined) {
		return '';
	}
	const s = String(value);
	if (/[",\r\n]/.test(s)) {
		return `"${s.replace(/"/g, '""')}"`;
	}
	return s;
}

export const MyLinks: FC = observer(() => {
	const { currentRouteWithoutSpecialChars } = useRoute();
	const [searchParams] = useSearchParams();
	const accountIdParam = searchParams.get('accountId');
	const { modalsStore, linksStore, toastsStore, publisherStore } = useStores<IMFPPStore>();
	const { isLoading, error, data: linksData, accountId: contextAccountId, refetchMyLinks } = useMyLinksList();
	const { FiltersActions } = useMyLinksActions();
	const [isLoadingCsv, setIsLoadingCsv] = useState(false);

	const accountList = publisherStore.getListStore().getData();
	const accountSelected = useMemo((): IAccount | null => {
		if (!accountIdParam || !Array.isArray(accountList) || !accountList.length) {
			return null;
		}
		return accountList.find(account => `${account?.id}` === accountIdParam) ?? null;
	}, [accountIdParam, accountList]);
	const canManageSelfServeLinks = accountSelected?.allowSelfLinkGeneration === true;

	const onDownloadMyLinksFile = () => {
		const sortedRows = linksStore.getSortedRows();
		if (sortedRows.length <= 0) {
			toastsStore.addToast({
				msg: 'Cannot export empty file.',
				type: DispatchLogsTypes.Info,
			});
			return;
		}
		setIsLoadingCsv(true);
		const account = publisherStore
			.getListStore()
			.getData()
			?.find(a => accountIdParam && `${a.id}` === accountIdParam);
		const fileName = `Manifesto my links - ${account?.name || accountIdParam || 'export'} - ${dayjs().format('YYYY/MM/DD')}`;
		const csvColumns: (keyof IPublisherLink)[] = [
			'id',
			'domain',
			'geo',
			'linkType',
			'displayStatus',
			'createdAt',
			'stoppedAt',
			'trackingUrl',
		];
		const headers = csvColumns.join(',');
		const rows = sortedRows.map(r => csvColumns.map(col => csvEscape(r.original[col])).join(',')).join('\n');
		const csvContent = `${headers}\n${rows}`;
		const blob = new Blob([csvContent], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', `${fileName}.csv`);
		link.style.display = 'none';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
		setIsLoadingCsv(false);
	};

	const handleStop = useCallback(
		(link: IPublisherLink) => {
			if (!canManageSelfServeLinks) {
				return;
			}
			modalsStore.addModal({
				children: (
					<StopLinkModal
						onConfirm={() => {
							const publisherId = link.publisherId ?? contextAccountId;
							if (publisherId == null) {
								return;
							}
							LinksApi.stopLink(link.id, publisherId)
								.then(() => {
									const updated = linksStore
										.getLinks()
										.map(l =>
											l.id === link.id ? { ...l, status: 'STOPPED' as const, displayStatus: 'Stopped' as const } : l
										);
									linksStore.setLinks(updated);
								})
								.catch((err: unknown) => {
									if (isAbortError(err as IAskError)) {
										return;
									}
									const apiErr = err as ILinksApiCallError;
									toastsStore.addToast({
										msg: apiErr?.message || 'An error occurred while stopping the link.',
										type: DispatchLogsTypes.Error,
									});
									refetchMyLinks();
								});
						}}
					/>
				),
			});
		},
		[canManageSelfServeLinks, contextAccountId, linksStore, modalsStore, refetchMyLinks, toastsStore]
	);

	const handleRegenerate = useCallback(
		(link: IPublisherLink) => {
			if (!canManageSelfServeLinks) {
				return;
			}
			modalsStore.addModal({
				children: (
					<RegenerateLinkModal
						onConfirm={() => {
							const publisherId = link.publisherId ?? contextAccountId;
							if (publisherId == null) {
								return;
							}
							LinksApi.regenerateLink(link.id, publisherId)
								.then(() => {
									const updated = linksStore.getLinks().map(l => {
										if (l.id !== link.id) {
											return l;
										}
										return {
											...l,
											status: 'ACTIVE' as const,
											displayStatus: 'Active' as const,
											stoppedAt: undefined,
										};
									});
									linksStore.setLinks(updated);
								})
								.catch((err: unknown) => {
									if (isAbortError(err as IAskError)) {
										return;
									}
									const apiErr = err as ILinksApiCallError;
									toastsStore.addToast({
										msg: apiErr?.message || 'An error occurred while regenerating the link.',
										type: DispatchLogsTypes.Error,
									});
									refetchMyLinks();
								});
						}}
					/>
				),
			});
		},
		[canManageSelfServeLinks, contextAccountId, linksStore, modalsStore, refetchMyLinks, toastsStore]
	);

	const { columns } = useMyLinksHeaders({
		onStop: handleStop,
		onGenerate: handleRegenerate,
		canManageSelfServeLinks,
	});

	return (
		<Page unstyled={true} className={styles.article}>
			<Helmet>
				<title>Manifesto - My Links</title>
			</Helmet>
			<Pagebar classes={{ pagebar: styles.padding }} debugProps={{ dataAttrs: [new DataAttribute('id', 'pagebar')] }}>
				<PagebarTitle>My Links</PagebarTitle>
			</Pagebar>
			<ControlledTable
				onExportToCsv={() => {
					sendGtagEvent({
						action: TrackingActions.Click,
						category: EVENTS.CLICK.INDEX_PAGE.ACTION_LINER.EXPORT,
						label: 'Export My Links CSV',
						value: window.location.href,
					});
					onDownloadMyLinksFile();
				}}
				isLoadingCsv={isLoadingCsv}
				currentRoute={currentRouteWithoutSpecialChars}
				defaultSortBy={defaultSortBy}
				isGlobalFilter={true}
				classes={{ wrapper: `${styles.padding} ${myLinksTableStyles.myLinksTableTight}` }}
				label={'My Links'}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'my_links_table')] }}
				columns={columns}
				data={linksData || []}
				isLoading={isLoading}
				isError={Boolean(error)}
				getRowClassName={row => {
					const link = row.original as IPublisherLink;
					return link.displayStatus !== 'Active' ? myLinksTableStyles.rowInactive : undefined;
				}}>
				{({ sortedRows }) => {
					linksStore.setSortedRows(sortedRows);
					return {
						FiltersActions,
					};
				}}
			</ControlledTable>
		</Page>
	);
});
